/*
 * File: SaveOrder.js
 * Project: mp-mobile-app
 *
 * Created by Rob Rothe
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Mobx state tree saving methods
 */
import Api from "../../Api";
import { flow, getSnapshot, getParent, hasParent } from "mobx-state-tree";
import mstIsNumber from "utils/MSTIsNumber";
import DateFormatter from "utils/DateFormatter";
import PatientApi from "services/patients/store/Api";
import OrderReferences from "../OrderReferences";
import PatientStore from "services/patients/store/PatientStore";

const OrderActions = self => ({
  populateExistingPatient: flow(function*(storeManager, providerOfficeStore) {
    const patientStore = storeManager.getStore(PatientStore);
    const patientId = patientStore.patientId;
    if (patientId) {
      self.setPatient(patientStore.patientModel);
      yield self.populateIncidents(storeManager, patientId);
      yield self.populateDefaultIncident(patientId);
      yield self.populateProviderOffice(providerOfficeStore);
    }
  }),

  populateIncidents: flow(function*(storeManager, patientId) {
    yield storeManager.getStore(OrderReferences).getIncidents(patientId);
  }),

  populateDefaultIncident: flow(function*(patientId) {
    const incident = yield PatientApi.currentIncidents(patientId);
    self.incidentId = incident.incidentId;
  }),

  populateProviderOffice: flow(function*(providerOfficeModel) {
    yield providerOfficeModel.getProviderOfficeFeeData();
    self.providerId = providerOfficeModel.patientProvider.userId;
    self.officeId = providerOfficeModel.patientOffice.id;
  }),
  /**
   * Sets the patient information in the model from the patient search component
   *
   * @param {Object} patient object with patient info
   */
  setPatient(patient) {
    self.patientId = patient.patientId;
    self.patientFirst = patient.firstName;
    self.patientLast = patient.lastName;
  },

  /**
   * Set the model property with a new value
   *
   * @param {String} key
   * @param {Mixed} val
   */
  onChange(key, val) {
    self[key] = mstIsNumber(self, key, val);
  },

  /**
   * Called from components to create/update an order based on
   * if the models ordersId > 0
   */
  save: flow(function*() {
    if (self.ordersId > 0) {
      return yield self.update();
    }
    return yield self.create();
  }),

  // -------------------------------------------------------
  // private functions

  /**
   * Data to scrub and grab a snopshot before sending to the api
   * TODO:  Remove the order notes empty string when the order notes get/post
   *    types match
   *
   * @return {Object}
   */
  prepareSave() {
    let snapshot = Object.assign({}, getSnapshot(self));
    if (self.incidentId === 0) snapshot.incidentId = undefined;
    if (self.due) snapshot.due = DateFormatter.forApi(self.due);
    snapshot["orderNotes"] = undefined;
    return snapshot;
  },

  /**
   * Creates a new order
   */
  create: flow(function*() {
    let snapshotForApi = self.prepareSave();
    try {
      const res = yield Api.create(snapshotForApi);

      //Update snapshot with id generated on server
      snapshotForApi.ordersId = res.ordersId;
      if (hasParent(self)) {
        getParent(self).create(snapshotForApi);
      }
      return snapshotForApi;
    } catch (error) {
      console.log("Error creating order", error);
    }
  }),

  /**
   * Updates an order
   */
  update: flow(function*() {
    const snapshotForApi = self.prepareSave();
    try {
      yield Api.update(snapshotForApi);
      if (hasParent(self)) {
        getParent(self).update(snapshotForApi);
      }
      return snapshotForApi;
    } catch (error) {
      console.log("Error updating order", error);
    }
  })
});

export default OrderActions;
